import {NavLink} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import HomeIcon from '../../images/home.svg'
import LeftArrow from '../../images/left_arrow.svg'
import {BllinkLogger} from '../../common/utils/bllink_loggers';
import { constants } from "../../common/constants/constants";
import {GAEvent} from "../../common/google_analytics/google_analytics";
import CacheData from "../../common/utils/cacheData";
import {BuildingItem, BuildStructure, AllBuildingsStyled} from './allBuildings.styled';
import {hideLoader, showLoader} from '../../store/common/slice';
import BllinkUtils from '../../common/utils/utils';
import useBodyClass from '../../common/hooks/useBodyClassName';
import {useAccessToken} from '../../common/hooks/useAccessToken';
import Button from '../../common/Button';
import ErrorsHandler from "../../common/errors/handleErrors";

const Money = BllinkUtils.Money;
const StringUtils = BllinkUtils.Strings;

export default function AllBuildings({history}) {
    const {t} = useTranslation();
    document.title = t('titles.all_buildings');
    useBodyClass('all_buildings');

    const dispatch = useDispatch();
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const allBuildings = useSelector((state) => state.manager.buildings);

    const [partner, setPartner] = useState({});
    const [mcData, setMcData] = useState();
    const [buildingsFilter, setBuildingsFilter] = useState('');

    const client = useAccessToken();

    useEffect(async () => {
        if (allBuildings.partner?.partnerID != null ?? false) {
            dispatch(showLoader());
            const mcStatsData = await getMCData(allBuildings.partner.partnerID);
            dispatch(hideLoader());
            setMcData(mcStatsData);
            setPartner(allBuildings.partner);
        }
    }, [allBuildings]);

    useEffect(() => {
            redirectToLastPageIfNeeded();
            const {buildings, partner} = allBuildings;
            if (buildings && buildings.length === 1) {
                GAEvent(constants.GoogleAnalytics.Events.allBuildings, 'one_building', 1, 'one_building');
                history.push({pathname: `/${constants.pages.buildingManagerProfileUrl}/${buildings[0].buildingID}`});
            }
            reportBuildingsToAnalytics(buildings && buildings.length);
    }, [allBuildings]);

    const buildings = useMemo(() => {
        const enabledBuildings = allBuildings.buildings?.filter(building => !building.disabled) ?? [];
        return enabledBuildings.sort((a, b) => a.title.trim() > b.title.trim() ? 1 : -1);
    }, [allBuildings]);

    const filteredBuildings = useMemo(() => {
        if (buildingsFilter === '' || buildingsFilter == null) {
            return buildings;
        }
        return buildings.filter(item => item.title.includes(buildingsFilter))
    }, [buildings, buildingsFilter]);

    const dataLoaded = useMemo(() => allBuildings?.buildings && allBuildings?.buildings.length > 1, [allBuildings]);


    const getMCData = async (partnerId) => {
        try {
            const data = await client.partners.dashboard.getStats(partnerId);
            return data;
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
            return { partnerStats: {} };
        }
    }

    const redirectToLastPageIfNeeded = () => {
        let lastPage = CacheData.fetchCache('lastPage');
        if (lastPage) {
            BllinkLogger.info(`redirecting to lastPage ${lastPage}`)
            CacheData.delete('lastPage');
            // temp disabled to check login bug with new users
            window.location.href = lastPage;
        }
    };

    const reportBuildingsToAnalytics = numberOfBuildings => {
        let range = '...'
        range = numberOfBuildings < 5 ? 'under_5' :
            numberOfBuildings < 10 ? 'under_10' :
                numberOfBuildings < 15 ? 'under_15' :
                    numberOfBuildings < 50 ? 'under_50' :
                        numberOfBuildings < 75 ? 'under_75' :
                            numberOfBuildings < 100 ? 'under_100' :
                                numberOfBuildings > 100 ? 'over_100' : '';

        GAEvent(constants.GoogleAnalytics.Events.allBuildings, `buildings_range`, 1, `buildings_${range}`);
    };

    const singleBuildingItem = (building, index) => {
        const stats = mcData && mcData?.allBuildingStats?.[building?.buildingID];
        let hoursSaved, currency, paymentsThisMonthsValue, totalOneTimeDebtsValue, totalMonthsDebtsValue, totalForManualDepositValue, currencyClassName = '';
        if (stats) {
            hoursSaved = Money.roundNumber(stats.hoursSaved,0 );
            const {paymentsThisMonth, totalOneTimeDebts, totalMonthsDebts, totalForManualDeposit} = stats;
            currency = t(`currency.${paymentsThisMonth.currency}`);
            paymentsThisMonthsValue = StringUtils.numberWithCommas(Money.roundNumber(paymentsThisMonth.value, 0));
            totalOneTimeDebtsValue = StringUtils.numberWithCommas(Money.roundNumber(totalOneTimeDebts.value, 0));
            totalMonthsDebtsValue = StringUtils.numberWithCommas(Money.roundNumber(totalMonthsDebts.value, 0));
            // todo remove if after deployed to backend
            totalForManualDepositValue = totalForManualDeposit ? StringUtils.numberWithCommas(Money.roundNumber(totalForManualDeposit.value, 0)): 0;
        }
        currencyClassName = `currency_header ${StringUtils.checkReverseCurrencyOrder(currency, t) ? 'turn_currency' : ''}`

        return (
            <div className="col-md-6" key={building.buildingID}>
                <OverlayTrigger
                    placement="top"
                    overlay={props => (
                        <Tooltip {...props}>
                            {
                                stats && (
                                    <div>
                                        <p>
                                            <span>{t('buildings.total_payments_this_month')} </span>
                                            <span className={currencyClassName}><span>{paymentsThisMonthsValue}</span><span>{currency}</span></span>
                                        </p>
                                        <p>
                                            <span>{t('buildings.hours_saved')} </span>
                                            <span className='currency_header'>{hoursSaved} {t('buildings.hours')}</span>
                                        </p>
                                        <p>
                                            <span>{t('buildings.total_debts_for_one_time')} </span>
                                            <span className={currencyClassName}><span>{totalOneTimeDebtsValue}</span><span>{currency}</span></span>
                                        </p>
                                        <p>
                                            <span>{t('buildings.total_debts_for_monthly')} </span>
                                            <span className={currencyClassName}><span>{totalMonthsDebtsValue}</span><span>{currency}</span></span>
                                        </p>
                                        <p>
                                            <span>{t('buildings.total_manual_deposit')} </span>
                                            <span className={currencyClassName}><span>{totalForManualDepositValue}</span><span>{currency}</span></span>
                                        </p>
                                    </div>
                                )
                            }
                        </Tooltip>
                    )}
                >
                    <BuildingItem className="js-building-item" ltr={ltr}>
                        <NavLink className="set_pr_link" to={`/${constants.pages.buildingManagerProfileUrl}/${building.buildingID}`}>
                            <div className="cl_img_link_2">
                                <img src={HomeIcon} alt="home icon"/>
                            </div>
                            <div>
                                <span className="st_cl_sp">{t('buildings.building_x', {index: index + 1})}, </span>
                                <span>{building.title}</span>
                            </div>
                            <div className="cl_img_link_1 js-arrow-element d-none d-md-block">
                                <img src={LeftArrow} alt="left arrow" />
                            </div>
                        </NavLink>
                    </BuildingItem>
                </OverlayTrigger>
            </div>
        );
    }

    if (!dataLoaded) {
        return <div/>;
    }

    // if is partner
    let shouldShow = (partner.partnerID);

    const onOpenBuilidingClick = () => history.push({pathname: `${constants.pages.newBuildingUrl}`});

    return (
        <AllBuildingsStyled>
            <h1 className='page-title'>
                {t('buildings.your_buildings')}
            </h1>
            <div className="container">
                <div className="row">
                    <div className="search_build col-md-12">
                        <p>{t('buildings.your_buildings_sub_title')}</p>
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="col-md-12">
                    <div className="row justify-content-between align-items-center">
                        <div className="col-sm-6 col-12" id="build_left">
                            <div className="form-group search-building">
                                <label className='label' htmlFor="first-name">{t('buildings.building_address')}:</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="building-add"
                                    id="building_add_2"
                                    onInput={(evt) => setBuildingsFilter(evt.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-sm-6 col-12">
                            <Button className="ml-auto open-building-btn" onClick={onOpenBuilidingClick}>{t('buildings.open_building')}</Button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container">
                <BuildStructure className="col-md-12">
                    <h3 className={'block-title'}>{t('buildings.you_have_x_active_buildings', {number: buildings.length})}</h3>
                    <div className="col-md-12">
                        <div className="row">
                            {filteredBuildings.length ? filteredBuildings.map((building, index) => singleBuildingItem(building, index)) : null}
                        </div>
                    </div>
                </BuildStructure>
            </div>
        </AllBuildingsStyled>
    )
};
